import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import ImgCropWithAspect, { ImgCropProps } from "antd-img-crop";
import ImgCropWithoutAspect from "antd-img-crop-custom";
import Upload from "antd/es/upload";
import { RcFile } from "antd/es/upload/interface";
import cn from "classnames";
import { UploadRequestOption } from "rc-upload/lib/interface";
import { useIntl } from "react-intl";

import { RawFile } from "@mapmycustomers/shared/types/File";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import { createFile } from "@app/store/files/actions";
import { isFileCreating } from "@app/store/files/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./ImageUploader.module.scss";

interface Props {
  aspect?: false | ImgCropProps["aspect"];
  createFile: typeof createFile.request;
  image?: RawFile;
  imageContainerClassName?: string;
  isFileCreating: boolean;
  noImageText: string;
  onChange?: (image?: null | RawFile) => void;
  sizeLimit: number;
}

const ImageUploader: React.FC<Props> = ({
  aspect,
  createFile,
  image,
  imageContainerClassName,
  isFileCreating,
  noImageText,
  onChange,
  sizeLimit,
}) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();
  const [errors, setErrors] = useState<string[]>([]);
  const [dragging, setDraggingTrue, setDraggingFalse] = useBoolean();

  const handleCustomRequest = useCallback(
    async ({ file }: UploadRequestOption) => {
      createFile({ callback: onChange, file: file as Blob | File });
    },
    [createFile, onChange]
  );

  const handleRemoveImage = useCallback(
    (e) => {
      stopEvents(e);
      onChange?.(null);
      analyticIssuer?.clicked(["Remove"]);
    },
    [analyticIssuer, onChange]
  );

  const beforeUpload = useCallback(
    (file: RcFile) => {
      analyticIssuer?.clicked(["Upload"]);
      const errors = [
        ...(["image/jpeg", "image/png"].includes(file.type)
          ? []
          : [
              intl.formatMessage({
                id: "editProfilePicture.error.format",
                defaultMessage: "File not supported. Please try again.",
                description: "Setting upload photo format error",
              }),
            ]),
        ...(file.size < sizeLimit
          ? []
          : [
              intl.formatMessage({
                id: "editProfilePicture.error.size",
                defaultMessage: "File too large. Please try again. ",
                description: "Setting upload photo size error",
              }),
            ]),
      ];
      setErrors(errors);
      return errors.length === 0;
    },
    [analyticIssuer, intl, setErrors, sizeLimit]
  );

  const ImgCrop = aspect === false ? ImgCropWithoutAspect : ImgCropWithAspect;

  return (
    <ImgCrop
      aspect={aspect || 1}
      modalTitle={intl.formatMessage({
        id: "editProfilePicture.crop.title",
        defaultMessage: "Edit image",
        description: "Crop/rotate user profile photo modal title",
      })}
      rotationSlider
    >
      <Upload
        beforeUpload={beforeUpload}
        customRequest={handleCustomRequest}
        fileList={[]}
        multiple={false}
      >
        <div
          className={cn(styles.container, {
            [styles.draggingContainer]: dragging,
            [styles.erroredContainer]: errors.length > 0,
          })}
          onDragEnter={setDraggingTrue}
          onDragLeave={setDraggingFalse}
        >
          <div
            className={cn(
              styles.imageContainer,
              imageContainerClassName,
              image?.publicURI && styles.transparentContainer
            )}
          >
            {isFileCreating ? (
              <LoadingSpinner mini />
            ) : image?.publicURI ? (
              <img alt="preview" className={styles.image} src={image.publicURI} />
            ) : (
              <div>{noImageText}</div>
            )}
          </div>
          <div>
            <div className={styles.title}>
              {intl.formatMessage(
                {
                  id: "settings.fileUploader.title",
                  defaultMessage: "Drag and drop an image, or <a>upload</a>",
                  description: "Title of settings file uploader",
                },
                { a: (text: string[]) => <ButtonLink>{text}</ButtonLink> }
              )}
            </div>
            <div className={styles.subTitle}>
              {intl.formatMessage(
                {
                  id: "settings.fileUploader.subTitle",
                  defaultMessage: "We support .jpeg and .png images up to {limit, number, ::K}MB",
                  description: "Sub Title of settings file uploader",
                },
                { limit: sizeLimit / (1024 * 1024) }
              )}
            </div>
            {image?.publicURI && (
              <div>
                <ButtonLink className={styles.removeLink} onClick={handleRemoveImage}>
                  {intl.formatMessage({
                    id: "settings.fileUploader.removeImage",
                    defaultMessage: "Remove image",
                    description: "Remove image text of settings file uploader",
                  })}
                </ButtonLink>
              </div>
            )}
          </div>
        </div>
        <div className={styles.errorList}>
          {errors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      </Upload>
    </ImgCrop>
  );
};

const mapStateToProps = (state: RootState) => ({
  isFileCreating: isFileCreating(state),
});

const mapDispatchToProps = {
  createFile: createFile.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ImageUploader);
